import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { fetchPriceSummary } from '../api/api';
import { useStorage } from '../contexts/StorageContext';
import type { PriceSummary } from '../types/priceSummary';
import { onPriceSummaryStale, priceSummaryPollMs } from '../utils/priceSummary';

function clearTimer(ref: MutableRefObject<number | null>): void {
  if (ref.current != null) { window.clearTimeout(ref.current); ref.current = null; }
}

export interface PriceSummaryState {
  /** 지금 매장의 요약 — 모름(첫 로딩 · 오류 · 옛 서버 · 모양이 다름)이면 null. */
  summary: PriceSummary | null;
  /** 옛 서버(404) — 폴링하지 않고 건수를 비운 채로 둔다. */
  unsupported: boolean;
}

/**
 * 가격 화면 칩 건수 요약(감사 card-controltower#745, 백엔드 PR #93) — `enabled` 인 동안 받고 `cacheSeconds` 간격으로 다시 받는다.
 *   - 매장(SUPER 매장 전환) · 로그인이 바뀌면 지난 값을 버리고 새로 받는다 — 늦게 온 옛 매장 응답은 버린다.
 *   - SUPER 가 매장을 고르지 않았으면 부르지 않는다(백엔드 400 "매장 지정 필요").
 *   - 옛 서버(404)면 멈춘다. 그 밖 오류는 건수를 모름으로 두고(0 으로 위장하지 않는다) 다음 간격에 다시 받는다.
 *   - 탭이 가려져 있으면 받지 않고, 다시 보이면 바로 받는다.
 *   - 매장 가격 요청 · 검토 저장 뒤(`markPriceSummaryStale`)에는 간격을 기다리지 않고 받는다.
 */
export function usePriceSummary(enabled: boolean): PriceSummaryState {
  const { isLoggedIn, isSuper, store, superStore, username } = useStorage();
  const scopeStore = isSuper ? superStore : store;
  // 계정이 바뀌어도(같은 매장의 다른 계정) 처음부터 — 이전 사용자 응답이 남지 않게.
  const scope = enabled && isLoggedIn && scopeStore ? `${isSuper ? 'super' : 'store'}:${username ?? ''}:${scopeStore}` : null;

  const [state, setState] = useState<PriceSummaryState>({ summary: null, unsupported: false });
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  // 요청 순번 — 겹친 요청은 마지막 것만 화면에 쓴다(쓰기 뒤 즉시 갱신과 폴링이 겹칠 때 옛 응답이 새 값을 덮지 않게).
  const seqRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const missedRef = useRef(false);
  const unsupportedRef = useRef(false);

  const load = useCallback(async () => {
    const at = scopeRef.current;
    if (!at || unsupportedRef.current) return;
    clearTimer(timerRef);
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') { missedRef.current = true; return; }
    missedRef.current = false;
    const seq = ++seqRef.current;
    let next: PriceSummary | null = null;
    try {
      next = await fetchPriceSummary() ?? null;
      if (seq !== seqRef.current || at !== scopeRef.current) return;
      setState({ summary: next, unsupported: false });
    } catch (e) {
      if (seq !== seqRef.current || at !== scopeRef.current) return;
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        unsupportedRef.current = true;
        setState({ summary: null, unsupported: true });
        return;
      }
      setState({ summary: null, unsupported: false });
    }
    // 다음 받기 — 이 응답의 cacheSeconds(모르면 기본)만큼 뒤.
    timerRef.current = window.setTimeout(() => { void load(); }, priceSummaryPollMs(next));
  }, []);

  // 매장 · 로그인 · 켜짐이 바뀌면 처음부터.
  useEffect(() => {
    seqRef.current += 1; // 진행 중인 옛 응답을 버린다
    clearTimer(timerRef);
    unsupportedRef.current = false;
    missedRef.current = false;
    setState({ summary: null, unsupported: false });
    if (scope) void load();
    // 떠날 때 · 매장이 바뀔 때 진행 중인 응답이 화면에 쓰거나 다음 받기를 걸지 않게 순번도 올린다.
    return () => { seqRef.current += 1; clearTimer(timerRef); };
  }, [scope, load]);

  // 쓰기 뒤 즉시 갱신 · 다시 보일 때.
  useEffect(() => {
    if (!scope) return undefined;
    const off = onPriceSummaryStale(() => { void load(); });
    const onVisible = () => {
      if (document.visibilityState === 'visible' && missedRef.current) void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => { off(); document.removeEventListener('visibilitychange', onVisible); };
  }, [scope, load]);

  return state;
}
